import React, { useEffect, useState, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import './Report.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const Report = ({ report, filename, pdfBase64, pdfFilename, onNewAnalysis }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [containerWidth, setContainerWidth] = useState(null);
  const pageRefs = useRef({});
  const viewerRef = useRef(null);

  useEffect(() => {
    if (!pdfBase64) {
      setPdfUrl(null);
      return undefined;
    }

    const cleanBase64 = pdfBase64.includes(',')
      ? pdfBase64.split(',')[1]
      : pdfBase64.replace(/^[\s]+|[\s]+$/g, '');

    try {
      const byteCharacters = atob(cleanBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(blob);
      setPdfUrl(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    } catch (error) {
      console.error('Failed to convert PDF base64 to blob:', error);
      setPdfUrl(null);
      return undefined;
    }
  }, [pdfBase64]);

  function onDocumentLoadSuccess({ numPages: nextNumPages }) {
    setNumPages(nextNumPages);
    setCurrentPage(1);
  }

  useEffect(() => {
    const updateWidth = () => {
      const viewerElement = document.querySelector('.pdf-main-viewer');
      if (viewerElement) {
        setContainerWidth(viewerElement.offsetWidth - 80); // Subtract padding
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [pdfUrl]);

  // Track current page based on scroll position
  useEffect(() => {
    if (!numPages || !viewerRef.current) return undefined;

    const handleScroll = () => {
      const viewer = viewerRef.current;
      if (!viewer) return;

      const viewerRect = viewer.getBoundingClientRect();
      const viewerTop = viewerRect.top;
      const viewerMiddle = viewerTop + viewerRect.height / 2;

      let closestPage = 1;
      let closestDistance = Infinity;

      // Find which page is closest to the middle of the viewport
      Object.keys(pageRefs.current).forEach((pageNum) => {
        const pageElement = pageRefs.current[pageNum];
        if (pageElement) {
          const pageRect = pageElement.getBoundingClientRect();
          const pageMiddle = pageRect.top + pageRect.height / 2;
          const distance = Math.abs(pageMiddle - viewerMiddle);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestPage = parseInt(pageNum, 10);
          }
        }
      });

      setCurrentPage(closestPage);
    };

    const viewer = viewerRef.current;

    // Initial check after a short delay to ensure pages are rendered
    const timeoutId = setTimeout(handleScroll, 300);

    // Listen to scroll events
    viewer.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeoutId);
      viewer.removeEventListener('scroll', handleScroll);
    };
  }, [numPages]);

  const handleDownloadPdf = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = pdfFilename || (filename ? filename.replace(/\.(md|json)$/i, '.pdf') : 'report.pdf');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="report-container">
      <div className="report-header">
        <h1>Demo Gap Analysis</h1>
        <div className="report-actions">
          <button
            onClick={handleDownloadPdf}
            className="download-button"
            disabled={!pdfUrl}
            title={pdfUrl ? 'Download the generated PDF' : 'PDF will appear once conversion succeeds'}
          >
            📄 Download PDF
          </button>
          <a
            href="https://www.testbox.com/demo"
            className="schedule-call-button"
            target="_blank"
            rel="noopener noreferrer"
            title="Schedule a call with our team"
          >
            📅 Schedule a call with our team
          </a>
          <button onClick={onNewAnalysis} className="new-analysis-button">
            ➕ New Analysis
          </button>
        </div>
      </div>

      <div className="report-content single-preview">
        <div className={`pdf-preview ${pdfUrl ? '' : 'placeholder'}`}>
          <div className="pdf-preview-header">
            <h2>PDF Preview</h2>
            {pdfUrl && <span className="pdf-filename">{pdfFilename || 'report.pdf'}</span>}
          </div>
          {pdfUrl ? (
            <div className="pdf-viewer-container">
              {/* Sidebar with page thumbnails */}
              <div className="pdf-sidebar">
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<div className="pdf-loading">Loading pages...</div>}
                >
                  {Array.from(new Array(numPages), (el, index) => (
                    <div
                      key={`thumbnail_${index + 1}`}
                      className={`pdf-thumbnail ${currentPage === index + 1 ? 'active' : ''}`}
                      onClick={() => {
                        const pageElement = pageRefs.current[index + 1];
                        if (pageElement) {
                          pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          const pageElement = pageRefs.current[index + 1];
                          if (pageElement) {
                            pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }
                      }}
                    >
                      <Page
                        pageNumber={index + 1}
                        width={100}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                      <div className="thumbnail-label">{index + 1}</div>
                    </div>
                  ))}
                </Document>
              </div>

              {/* Main PDF viewer */}
              <div className="pdf-main-viewer" ref={viewerRef}>
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<div className="pdf-loading">Loading PDF...</div>}
                  error={<div className="pdf-error">Failed to load PDF</div>}
                >
                  {Array.from(new Array(numPages), (el, index) => (
                    <div
                      key={`page_${index + 1}`}
                      ref={(el) => { pageRefs.current[index + 1] = el; }}
                      className="pdf-page-wrapper"
                    >
                      <Page
                        pageNumber={index + 1}
                        width={containerWidth || 800}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                      />
                    </div>
                  ))}
                </Document>

                {/* Page indicator */}
                {numPages && (
                  <div className="pdf-page-indicator">
                    Page {currentPage} of {numPages}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="pdf-placeholder-text">
              PDF is generating or unavailable. Please try downloading again in a moment.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Report;

